import { ReactElement } from 'react';
import { NavLink } from 'react-router-dom';
import { Badge, Tooltip } from '@digdir/designsystemet-react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { Routes } from '../../enums/Routes';
import classes from './SidebarItem.module.css';

type SidebarItemProps = {
  route: Routes;
  icon: ReactElement;
  children: string;
  onNavigate?: () => void;
  size?: 'md' | 'sm';
  collapsed?: boolean;
  /** Only current on its own path, not on the pages under it */
  end?: boolean;
  /** Unread notifications, shown next to the label. Nothing is shown for none. */
  count?: number;
};

/**
 * Lenke i sidemenyen. NavLink setter aria-current="page" på aktiv side.
 * Minimert: bare ikon. Teksten er fortsatt lenkens navn (skjult visuelt), og vises som tooltip.
 */
export const SidebarItem = ({
  route,
  icon,
  children,
  onNavigate,
  size = 'md',
  collapsed = false,
  end = false,
  count = 0,
}: SidebarItemProps): ReactElement => {
  const { t } = useTranslation();
  const unread = count > 0 && (
    <>
      <Badge
        aria-hidden
        className={cn(classes.count, { [classes.countOnIcon]: collapsed })}
        count={count}
        maxCount={99}
        data-color="danger"
        data-size="sm"
      />
      <span className="ds-sr-only">{t('sidebar.unread', { count })}</span>
    </>
  );
  const link = (
    <NavLink
      to={route}
      end={end}
      onClick={onNavigate}
      className={cn('ds-focus', classes.item, {
        [classes.small]: size === 'sm',
        [classes.collapsed]: collapsed,
      })}
    >
      <span className={classes.icon}>{icon}</span>
      <span className={cn(classes.label, { 'ds-sr-only': collapsed })}>{children}</span>
      {unread}
    </NavLink>
  );

  return (
    <li>
      {collapsed ? (
        <Tooltip content={children} placement="right">
          {link}
        </Tooltip>
      ) : (
        link
      )}
    </li>
  );
};
